import { useState, useEffect } from 'react';
import { doc, onSnapshot } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { auth, db } from '../firebaseUtils.js';
import { DEFAULT_CONFIG } from '../config/initialData.js';

/**
 * useConfig Hook
 * Fetches global system configurations from Firestore with real-time updates.
 * Falls back to local constants if database is unavailable.
 */
export const useConfig = () => {
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let configUnsubscribe = () => {};

    const authUnsubscribe = onAuthStateChanged(auth, (currentUser) => {
      configUnsubscribe();

      if (!currentUser) {
        setConfig(DEFAULT_CONFIG);
        setLoading(false);
        return;
      }

      // Listen to the single metadata document once the user is authenticated.
      const configRef = doc(db, "system_config", "metadata");

      configUnsubscribe = onSnapshot(
        configRef,
        (docSnap) => {
          if (docSnap.exists()) {
            const data = docSnap.data();
            // Merge with defaults so missing fields never break the UI.
            setConfig({ ...DEFAULT_CONFIG, ...data });
          } else {
            console.warn("System config document not found. Using defaults.");
            setConfig(DEFAULT_CONFIG);
          }
          setLoading(false);
        },
        (error) => {
          const isPermissionError =
            error?.code === 'permission-denied' ||
            String(error?.message || '').toLowerCase().includes('permission');

          if (isPermissionError) {
            console.warn("System config is not readable with the current Firestore rules. Using defaults.");
            setConfig(DEFAULT_CONFIG);
          } else {
            console.error("Error fetching system config:", error);
          }

          setLoading(false);
        }
      );
    });

    return () => {
      configUnsubscribe();
      authUnsubscribe();
    };
  }, []);

  return { config, loading };
};
